export const SENSORY_DOMAINS = [
  { id: "sounds", label: "Sounds" },
  { id: "lightsAndVisualInput", label: "Lights & Visual Input" },
  { id: "touchAndTextures", label: "Touch & Textures" },
  { id: "smells", label: "Smells" },
  { id: "tastes", label: "Tastes" },
  { id: "movement", label: "Movement" },
  { id: "bodyAwareness", label: "Body Awareness" },
  { id: "environmental", label: "Environmental" },
] as const;

export const SOUND_SENSITIVITY_OPTIONS = [
  { id: "loudMusic", label: "Loud music" },
  { id: "screaming", label: "Screaming" },
  { id: "suddenNoises", label: "Sudden noises" },
  { id: "overlappingVoices", label: "Overlapping voices" },
  { id: "other", label: "Other" },
] as const;

export const LIGHT_SENSITIVITY_OPTIONS = [
  { id: "brightLights", label: "Bright lights" },
  { id: "flashingLights", label: "Flashing lights" },
  { id: "visualClutter", label: "Visual clutter" },
  { id: "busyEnvironments", label: "Busy environments" },
  { id: "decorationsOrMovement", label: "Decorations/movement" },
  { id: "other", label: "Other" },
] as const;

export const TOUCH_SENSITIVITY_OPTIONS = [
  { id: "costumeFabrics", label: "Costume fabrics" },
  { id: "tagsOrSeams", label: "Tags/seams" },
  { id: "masks", label: "Masks" },
  { id: "facePaint", label: "Face paint" },
  { id: "unexpectedTouch", label: "Unexpected touch" },
  { id: "other", label: "Other" },
] as const;

export const SMELL_SENSITIVITY_OPTIONS = [
  { id: "perfumesOrColognes", label: "Perfumes/colognes" },
  { id: "foodOdors", label: "Food odors" },
  { id: "artificialScents", label: "Artificial scents" },
  { id: "strongSmells", label: "Strong smells" },
  { id: "other", label: "Other" },
] as const;

export const TASTE_SENSITIVITY_OPTIONS = [
  { id: "foodTextures", label: "Food textures" },
  { id: "strongFlavors", label: "Strong flavors" },
  { id: "unfamiliarCandyOrFoods", label: "Unfamiliar candy/foods" },
  { id: "temperature", label: "Temperature" },
  { id: "other", label: "Other" },
] as const;

export const MOVEMENT_SENSITIVITY_OPTIONS = [
  { id: "movementInCrowds", label: "Movement in crowds" },
  { id: "spinningOrMotion", label: "Spinning/motion" },
  { id: "unevenSurfaces", label: "Uneven surfaces" },
  { id: "escalatorsOrStairs", label: "Escalators/stairs" },
  { id: "carRides", label: "Car rides" },
  { id: "other", label: "Other" },
] as const;

export const BODY_AWARENESS_SENSITIVITY_OPTIONS = [
  { id: "physicalPressure", label: "Physical pressure" },
  { id: "hugsOrTouch", label: "Hugs/touch" },
  { id: "sittingStill", label: "Sitting still" },
  { id: "certainBodyPositions", label: "Certain body positions" },
  { id: "other", label: "Other" },
] as const;

export const ENVIRONMENTAL_SENSITIVITY_OPTIONS = [
  { id: "crowds", label: "Crowds" },
  { id: "heatOrHumidity", label: "Heat/humidity" },
  { id: "waitingInLine", label: "Waiting in line" },
  { id: "transitions", label: "Transitions" },
  { id: "unpredictableChanges", label: "Unpredictable changes" },
  { id: "other", label: "Other" },
] as const;

export const EARLY_SIGN_OPTIONS = [
  { id: "coversEars", label: "Covers ears" },
  { id: "becomesQuietOrWithdrawn", label: "Becomes quiet or withdrawn" },
  { id: "irritabilityOrComplaints", label: "Irritability or complaints" },
  { id: "pacingOrIncreasedMovement", label: "Pacing or increased movement" },
  {
    id: "difficultyFollowingDirections",
    label: "Difficulty following directions",
  },
  {
    id: "repeatingPhrasesOrQuestions",
    label: "Repeating phrases or questions",
  },
  { id: "tearfulOrUpset", label: "Tearful or upset" },
  { id: "asksToLeave", label: "Asks to leave" },
  { id: "hidesOrAvoidsPeople", label: "Hides or avoids people" },
  { id: "becomesRigidOrStuck", label: "Becomes rigid or stuck" },
  { id: "other", label: "Other" },
] as const;

export const WHAT_HELPS_OPTIONS = [
  {
    id: "noiseReducingHeadphones",
    label: "Use noise-reducing headphones or earplugs",
  },
  {
    id: "quieterRoutes",
    label: "Choose quieter routes or less crowded areas",
  },
  { id: "visitLessBusyTime", label: "Visit during a less busy time" },
  { id: "keepCostumesComfortable", label: "Keep costumes comfortable" },
  { id: "avoidFacePaintOrMasks", label: "Avoid face paint or masks" },
  { id: "familiarSnacksAndWater", label: "Bring familiar snacks and water" },
  {
    id: "regularBreaksQuietSpace",
    label: "Plan for regular breaks in a quiet space",
  },
  {
    id: "sunglassesOrHat",
    label: "Use sunglasses or a hat for bright lights",
  },
  {
    id: "advanceWarningsForTransitions",
    label: "Give advance warnings for transitions",
  },
  { id: "keepExpectationsSimple", label: "Keep expectations simple" },
  { id: "bringComfortItem", label: "Bring a comfort item" },
  { id: "other", label: "Other" },
] as const;

export type SensoryDomainId = (typeof SENSORY_DOMAINS)[number]["id"];
export type SoundSensitivityId =
  (typeof SOUND_SENSITIVITY_OPTIONS)[number]["id"];
export type LightSensitivityId =
  (typeof LIGHT_SENSITIVITY_OPTIONS)[number]["id"];
export type TouchSensitivityId =
  (typeof TOUCH_SENSITIVITY_OPTIONS)[number]["id"];
export type SmellSensitivityId =
  (typeof SMELL_SENSITIVITY_OPTIONS)[number]["id"];
export type TasteSensitivityId =
  (typeof TASTE_SENSITIVITY_OPTIONS)[number]["id"];
export type MovementSensitivityId =
  (typeof MOVEMENT_SENSITIVITY_OPTIONS)[number]["id"];
export type BodyAwarenessSensitivityId =
  (typeof BODY_AWARENESS_SENSITIVITY_OPTIONS)[number]["id"];
export type EnvironmentalSensitivityId =
  (typeof ENVIRONMENTAL_SENSITIVITY_OPTIONS)[number]["id"];
export type EarlySignId = (typeof EARLY_SIGN_OPTIONS)[number]["id"];
export type WhatHelpsId = (typeof WHAT_HELPS_OPTIONS)[number]["id"];
